import {Component, OnInit, ViewChild} from '@angular/core';
import {GroundGroupsService} from '../../../core/services/ground-groups.service';
import {LayoutService} from '../../../core/data-sources/layout.service';
import {ActionService, TenantState} from '../../../core/services/action.service';
import {ConfirmModalComponent} from '../../../modals/confirm-modal/confirm-modal.component';
import {first} from 'rxjs/operators';
import {MatDialog} from '@angular/material/dialog';
import {ActivatedRoute, Router} from '@angular/router';
import {AddGroupModalComponent} from '../../../modals/add-group-modal/add-group-modal.component';
import {TenantService} from '../../../core/services/tenant.service';
import {PigeonholeService} from 'src/app/core/data-sources/v3/pigeonhole/pigeonhole.service';
import {PigeonholeGroup, PigeonholeMapPosition, PigeonholeType} from 'src/models/Pigeonhole';
import { ErrorService, ErrorsEnum } from 'src/app/core/services/error.service';
import { ActionPermissionsService } from 'src/app/core/services/action-permissions.service';
import { ActionsEnum } from 'src/app/core/data-sources/action-data-source.service';
import { UserRoles } from 'src/models/User';


export enum GroundGroupsMapState {
  DEFAULT= 'DEFAULT',
  ADDING_TAGS= 'ADDING_TAGS',
  GROUP_HIGHLIGHT= 'GROUP_HIGHLIGHT',
  EDIT_PIGEONHOLE_POSITIONS = 'EDIT_PIGEONHOLE_POSITIONS'
}

export enum SetupGroundScreenState {
  GENERAL_OVERVIEW = 'GENERAL_OVERVIEW',
  PIGEONHOLE_NAVIGATION = 'PIGEONHOLE_NAVIGATION',
  PIGEONHOLE_INTERNAL_ASSIGNMENT = 'PIGEONHOLE_INTERNAL_ASSIGNMENT'
}

export enum GroundGroupsMapActionName{
  SET_HIGHLIGHT_MODE= 'SET_HIGHLIGHT_MODE',
  SET_ADDING_TAGS_MODE= 'SET_ADDING_TAGS_MODE',
  SAVE_TAGS= 'SAVE_TAGS',
  REMOVE_GROUND_GROUP= 'REMOVE_GROUND_GROUP',
  RESET= 'RESET'
}
export interface GroundGroupsMapAction{
  action: GroundGroupsMapActionName;
  data?: any;
}

@Component({
  selector: 'app-setup-ground',
  templateUrl: './setup-ground.component.html',
  styleUrls: ['./setup-ground.component.scss']
})
export class SetupGroundComponent implements OnInit {

  @ViewChild('mapComponent', { static: true }) mapComponent: any;

  groups = [];
  pigeonholeGroups: PigeonholeGroup[] = [];
  map;
  tenantLevelsEnabled = false;

  state: GroundGroupsMapState = GroundGroupsMapState.DEFAULT;
  screenState: SetupGroundScreenState = SetupGroundScreenState.GENERAL_OVERVIEW;
  selectedPositionIds = new Set<string>();
  currentGroundGroupId: string;
  currentGroundGroupName: string;
  showSectorsList = true;
  showPigeonholeList = false;
  pigeonholePositions = [];
  pigeonholePositionsIds = new Set<string>();
  isPigeonholeListLoading = false;
  showPigeonholeMap = false;
  selectedPigeonholeId: string = null;
  selectedPigeonholeMap: PigeonholeMapPosition[] | null = null;
  isPigeonholeMapLoading = false;
  isSavingPigeonholeAssignments = false;
  selectedView: 'SECTORS' | 'PIGEONHOLES' = 'SECTORS';
  userRole: UserRoles = null;
  actionsEnum = ActionsEnum;

  constructor(
               private tenantsService: TenantService,
               private groundGroupsService: GroundGroupsService,
               private layoutService: LayoutService,
               private actionService: ActionService,
               public dialog: MatDialog,
               private router: Router,
               private route: ActivatedRoute,
               private pigeonholeService: PigeonholeService,
               private errorService: ErrorService,
               private actionPermissionsService: ActionPermissionsService
               ) {}

  get isGeneralOverviewView() {
    return this.screenState === SetupGroundScreenState.GENERAL_OVERVIEW;
  }

  get isPigeonholeSectorView() {
    return this.selectedView === 'PIGEONHOLES' && this.screenState === SetupGroundScreenState.PIGEONHOLE_INTERNAL_ASSIGNMENT;
  }

  get canManageGroups() {
    return this.isGeneralOverviewView || this.isPigeonholeSectorView;
  }

  get displayedGroups() {
    return this.selectedView === 'PIGEONHOLES' ? this.pigeonholeGroups : this.groups;
  }

  get canSelectPigeonholeInternalPositions() {
    return this.screenState === SetupGroundScreenState.PIGEONHOLE_INTERNAL_ASSIGNMENT
      && this.state === GroundGroupsMapState.ADDING_TAGS
      && !!this.currentGroundGroupId
      && !this.isSavingPigeonholeAssignments;
  }

  async processSetAddingTagsMode(data){
    this.currentGroundGroupId = data.groundGroupId;
    const group = this.getGroupById(this.currentGroundGroupId);
    this.currentGroundGroupName = group.name;
    this.selectedPositionIds.clear();
    for ( const p of data.positions){
      this.selectedPositionIds.add(p);
    }
    return GroundGroupsMapState.ADDING_TAGS;
  }

  async processReset(){
    this.currentGroundGroupId = null;
    this.currentGroundGroupName = '';

    this.selectedPositionIds.clear();
    return GroundGroupsMapState.DEFAULT;
  }

  async processRemoveGroundGroup(){
    await this.removeGroundGroup();
    this.currentGroundGroupId = null;
    this.currentGroundGroupName = '';
    this.selectedPositionIds.clear();
    await this.loadData();
    return GroundGroupsMapState.DEFAULT;
  }


  async processSaveTags(){
    const has = this.someGroundPositionHasDifferentType();
    let upload = true;
    if (has){
      const response = await this.openConfirmRemoveModal();
      upload = response === 'SUBMITTED';
    }

    if (upload){
      await this.updateGroundGroup();
    }

    this.currentGroundGroupId = null;
    this.currentGroundGroupName = '';
    this.selectedPositionIds.clear();
    await this.loadData();
    return GroundGroupsMapState.DEFAULT;
  }

  async processSetHighlightMode(data){
    this.currentGroundGroupId = data.groundGroupId;
    const group = this.getGroupById(this.currentGroundGroupId);
    this.currentGroundGroupName = group.name;
    this.selectedPositionIds.clear();
    for (const p of data.positions){
      this.selectedPositionIds.add(p);
    }
    return GroundGroupsMapState.GROUP_HIGHLIGHT;
  }

  processAction(currentState: GroundGroupsMapState, action: GroundGroupsMapAction): Promise<GroundGroupsMapState>{
    if (!action){
      return Promise.resolve(currentState);
    }
    switch (currentState) {
      case GroundGroupsMapState.DEFAULT:
        if (action.action === GroundGroupsMapActionName.SET_ADDING_TAGS_MODE) {
          return this.processSetAddingTagsMode(action.data);
        }
        else if (action.action === GroundGroupsMapActionName.SET_HIGHLIGHT_MODE) {
          return this.processSetHighlightMode(action.data);
        }
        else { console.log('not allowed', action); }
        break;
      case GroundGroupsMapState.GROUP_HIGHLIGHT:
        if (action.action === GroundGroupsMapActionName.RESET) {
          return this.processReset();
        }
        else if (action.action === GroundGroupsMapActionName.SET_ADDING_TAGS_MODE) {
          return this.processSetAddingTagsMode(action.data);
        }
        else { console.log('not allowed', action); }
        break;
      case GroundGroupsMapState.ADDING_TAGS:
        if (action.action === GroundGroupsMapActionName.RESET) {
          return this.processReset();
        }
        else if (action.action === GroundGroupsMapActionName.SAVE_TAGS) {
          return this.processSaveTags();
        }
        else if (action.action === GroundGroupsMapActionName.REMOVE_GROUND_GROUP){
          return this.processRemoveGroundGroup();
        }
        else { console.log('not allowed', action); }
        break;
    }
    return Promise.resolve(currentState);
  }

  ngOnInit(): void {
    this.userRole = this.route.snapshot.data.role;
    this.loadData();
  }

  async loadData(){
    const p1 = this.groundGroupsService.getGroundGroups(true).then(res => {
      this.groups = res;
      return res;
    });
    const p2 = this.layoutService.getMap().then(res => {
      this.map = res;
      return res;
    });
    const p3 = this.tenantsService.get().then(res =>{
      this.tenantLevelsEnabled = res.levelsEnabled;
      return res;
    });
    const p4 = this.pigeonholeService.getPigeonholeGroups().then(res => {
      this.pigeonholeGroups = res.values || [];
      return res;
    });
    return Promise.all([p1, p2, p3, p4]);
  }

  async groupHighLighted(event){
    if (!this.isGeneralOverviewView) {
      return;
    }

    let action;
    const {hover, id} = event;
    switch (this.state) {
      case GroundGroupsMapState.DEFAULT:
        const group = this.getGroupById(id);
        const positions = group.positions.map(p => p.rfId);
        action = {
          action: GroundGroupsMapActionName.SET_HIGHLIGHT_MODE,
          data: {
            groundGroupId: id,
            positions
          }
        };
        break;
      case GroundGroupsMapState.GROUP_HIGHLIGHT:
        if (this.currentGroundGroupId !== id || hover === false){
          action = {
            action: GroundGroupsMapActionName.RESET
          };
        }
        break;
    }
    this.state = await this.processAction(this.state, action);
  }

  async groupClicked(groundGroupId){
    if (this.screenState === SetupGroundScreenState.PIGEONHOLE_INTERNAL_ASSIGNMENT) {
      if (!groundGroupId) {
        return;
      }

      const hasGroupChanged = this.currentGroundGroupId !== groundGroupId;
      this.currentGroundGroupId = groundGroupId;
      const group = this.getGroupById(groundGroupId);
      this.currentGroundGroupName = group?.name || '';
      this.state = GroundGroupsMapState.ADDING_TAGS;

      if (hasGroupChanged) {
        this.resetPigeonholeInternalSelection();
      }

      return;
    }

    let action;
    switch (this.state) {
      case GroundGroupsMapState.DEFAULT:
      case GroundGroupsMapState.GROUP_HIGHLIGHT:
        const group = this.getGroupById(groundGroupId);
        const positions = group.positions.map(p => p.rfId);
        action = {
          action: GroundGroupsMapActionName.SET_ADDING_TAGS_MODE,
          data: {
            groundGroupId,
            positions
          }
        };
        break;
      case GroundGroupsMapState.ADDING_TAGS:
        if (groundGroupId === this.currentGroundGroupId){
          action = {
            action: GroundGroupsMapActionName.SAVE_TAGS,
          };
        }
        break;
      default:
        action = null;
    }
    this.state = await this.processAction(this.state, action);
  }

  removeSector(){
    const group = this.getGroupById(this.currentGroundGroupId);
    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      panelClass: 'confirm-modal',
      autoFocus: false,
      data: { label: 'Deseja deletar o grupo:' + group.name }
    });
    return dialogRef.afterClosed().pipe(first()).subscribe(async (res) => {
      if (res === 'SUBMITTED'){
        let action;
        switch (this.state) {
          case GroundGroupsMapState.ADDING_TAGS:
            if (this.isPigeonholeSectorView) {
              await this.removePigeonholeGroup();
              this.state = GroundGroupsMapState.DEFAULT;
              return;
            }
            action = {
              action: GroundGroupsMapActionName.REMOVE_GROUND_GROUP,
              data: {
                groundGroupId:this.currentGroundGroupId,
              }
            };
            break;
          default:
            action = null;
        }
        this.state = await this.processAction(this.state, action);
      }
    });
  }

  positionSelected(position){
    if (this.selectedPositionIds.has(position.rfId)){
      this.selectedPositionIds.delete(position.rfId);
    } else{
      this.selectedPositionIds.add(position.rfId);
    }
    this.selectedPositionIds = new Set<string>(this.selectedPositionIds);
  }

  someGroundPositionHasDifferentType(){
    const group = this.getGroupById(this.currentGroundGroupId);
    for (const id of this.selectedPositionIds){
      const p = this.getPositionByRfId(id);
      if (p.type !== null && p.type !== group?.type) { return true; }
    }
    return false;
  }

  async updateGroundGroup(){
    const rfidTags: string[] = Array.from(this.selectedPositionIds) as string[];
    const payload = {
      groundPositionGroupId: this.currentGroundGroupId,
      rfidTags,
      overwrite: true
    };
    await this.actionService.setTagsToGroundGroup(payload);
  }

  async removeGroundGroup(){
    const payload = {
      groundPositionGroupId: this.currentGroundGroupId
    };
    await this.actionService.removeGroundGroup(payload);
  }

  async removePigeonholeGroup() {
    if (!this.currentGroundGroupId) {
      return;
    }

    try {
      const payload = { id: this.currentGroundGroupId };
      await this.actionService.removePigeonholeGroup(payload);
      this.currentGroundGroupId = null;
      this.currentGroundGroupName = '';
      this.selectedPositionIds.clear();
      this.loadData();
    } catch (e) {
      const errorType = e?.error?.body?.type || e?.error?.type || e?.type;
      if (errorType === ErrorsEnum.PIGEONHOLE_GROUP_ERROR_NOT_FOUND) {
        this.errorService.openErrorSnackBarWithCustomMessage('Setor de escaninho não encontrado.');
        return;
      }
      this.errorService.openErrorSnackBar(e);
    }
  }

  getGroupById(id){
    for (const g of this.displayedGroups){
      if (g.id === id) { return g; }
    }
    return null;
  }

  getPositionByRfId(rfId){
    for (const p of this.map){
      if (p.rfId === rfId) { return p; }
    }
    return null;
  }

  openConfirmRemoveModal() {
    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      panelClass: 'confirm-modal',
      autoFocus: false,
      data: { label: 'Existem Tags com tipos diferentes da do grupo! Sobrescrever?' }
    });
    return dialogRef.afterClosed().pipe(first()).toPromise();
  }

  openSavePigeonholeGroupModal() {
    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      panelClass: 'confirm-modal',
      autoFocus: false,
      data: { label: 'Tem certeza que deseja salvar?' }
    });
    return dialogRef.afterClosed().pipe(first()).toPromise();
  }

  openModal() {
    if (this.selectedView !== 'PIGEONHOLES') {
      const dialogRef = this.dialog.open(AddGroupModalComponent, {
        panelClass: 'confirm-modal',
      });
      dialogRef.afterClosed().pipe(first()).subscribe(async res => {
        if (res) {
          this.actionService.addGroundGroup(res).then(() => {
            this.loadData();
          });
        }
      });
    } else {
      const pigeonholeSectorOptions = [
        { value: PigeonholeType.STOCK, name: 'Estoque' },
        { value: PigeonholeType.EXPEDITION, name: 'Expedição' },
        { value: PigeonholeType.EXIT, name: 'Saída' },
        { value: PigeonholeType.PICKING, name: 'Separação' },
        { value: PigeonholeType.PICKING_MISCELLANEOUS, name: 'Separação Multi Produtos' }
      ];
      const dialogRef = this.dialog.open(AddGroupModalComponent, {
        panelClass: 'confirm-modal',
        data: { sectorOptions: pigeonholeSectorOptions }
      });
      dialogRef.afterClosed().pipe(first()).subscribe(async res => {
        if (res) {
          try {
            await this.actionService.addPigeonholeGroundGroup(res);
            this.loadData();
          } catch (e) {
            const errorType = e?.error?.body?.type || e?.error?.type || e?.type;
            if (errorType === ErrorsEnum.PIGEONHOLE_GROUP_ERROR_ALREADY_EXISTS) {
              this.errorService.openErrorSnackBarWithCustomMessage('Já existe um setor de escaninho com este nome.');
              return;
            }
            this.errorService.openErrorSnackBar(e);
          }
        }
      });
    }
  }

  async finish(){
    if(this.tenantLevelsEnabled){
      await this.actionService.setTenantState({state: TenantState.SETUP_LEVELS});
      await this.router.navigate(['/setup-levels']);
    } else{
      await this.actionService.setTenantState({state: TenantState.SETUP_REVIEW});
      await this.router.navigate(['/setup-finish']);
    }
  }
  async toSetup(){
    await this.router.navigate(['/setup']);
  }

  async handleSaveAction() {
    if (this.screenState === SetupGroundScreenState.PIGEONHOLE_INTERNAL_ASSIGNMENT) {
      await this.savePigeonholeInternalAssignments();
      return;
    }

    await this.groupClicked(this.currentGroundGroupId);
  }

  async savePigeonholeInternalAssignments() {
    if (!this.currentGroundGroupId || !this.pigeonholePositionsIds.size) {
      this.returnToGeneralOverview();
      return;
    }

    this.isSavingPigeonholeAssignments = true;
    try {
      const response = await this.openSavePigeonholeGroupModal();
      if (response !== 'SUBMITTED') {
        await this.loadData();
        this.returnToGeneralOverview();
        this.isSavingPigeonholeAssignments = false;
        return;
      }

      const selectedPigeonholeIds = Array.from(this.pigeonholePositionsIds) as string[];
      await Promise.all(
        selectedPigeonholeIds.map((pigeonholeId) =>
          this.actionService.setPigeonholeGroup({
            pigeonholeId,
            pigeonholeGroupId: this.currentGroundGroupId
          })
        )
      );

      await this.loadData();
      this.returnToGeneralOverview();
    } catch (e) {
      const errorType = e?.error?.body?.type || e?.error?.type || e?.type;
      if (errorType === ErrorsEnum.PIGEONHOLE_GROUP_ERROR_NOT_FOUND) {
        this.errorService.openErrorSnackBarWithCustomMessage('Setor de escaninho não encontrado.');
        return;
      }
      if (errorType === ErrorsEnum.PIGEONHOLE_GROUP_ERROR_PIGEONHOLE_NOT_FOUND) {
        this.errorService.openErrorSnackBarWithCustomMessage('Posição de escaninho não encontrada.');
        return;
      }
      this.errorService.openErrorSnackBar(e);
    
    } finally {
      this.isSavingPigeonholeAssignments = false;
    }
  }

  changeView(view: "SECTORS" | "PIGEONHOLES"){
    this.selectedView = view;

    switch (view) {
      case 'SECTORS':
        this.returnToGeneralOverview();
        break;
      case 'PIGEONHOLES':
        this.resetCurrentGroundGroupSelection();
        this.resetPigeonholeInternalSelection();
        this.resetGeneralSelection();
        this.setScreenState(SetupGroundScreenState.PIGEONHOLE_NAVIGATION);
        this.state = GroundGroupsMapState.EDIT_PIGEONHOLE_POSITIONS;
        this.closePigeonholeMap();
        this.fetchPigeonholePositions();
        break;
    }
  }

  async handlePigeonholeListPositionClick(pigeonholePosition) {
    const childPositions = await this.loadChildPigeonholePositions(pigeonholePosition);

    if (childPositions) {
      await this.openPigeonholeMap(pigeonholePosition, childPositions);
      this.setScreenState(SetupGroundScreenState.PIGEONHOLE_INTERNAL_ASSIGNMENT);
      this.state = GroundGroupsMapState.DEFAULT;
      this.resetCurrentGroundGroupSelection();
      this.resetPigeonholeInternalSelection();
    }
  }

  handlePigeonholeListGoBack() {
    if (this.isPigeonholeListLoading) {
      return;
    }

    this.returnToGeneralOverview();
  }

  async handlePigeonholeClicked(pigeonholePosition){
    const childPositions = await this.loadChildPigeonholePositions(pigeonholePosition);

    if (childPositions) {
      await this.openPigeonholeMap(pigeonholePosition, childPositions);
      this.setScreenState(SetupGroundScreenState.PIGEONHOLE_INTERNAL_ASSIGNMENT);
      this.state = GroundGroupsMapState.DEFAULT;
      this.resetCurrentGroundGroupSelection();
      this.resetPigeonholeInternalSelection();
    }
  }

  handlePigeonholeMapPositionClick(position) {
    if (!this.canSelectPigeonholeInternalPositions || !position?.id) {
      return;
    }

    if (this.pigeonholePositionsIds.has(position.id)) {
      this.pigeonholePositionsIds.delete(position.id);
    } else {
      this.pigeonholePositionsIds.add(position.id);
    }

    this.pigeonholePositionsIds = new Set<string>(this.pigeonholePositionsIds);
  }

  async openPigeonholeMap(pigeonholePosition, preloadedMap?: PigeonholeMapPosition[]) {
    if (!pigeonholePosition?.id) {
      return;
    }

    const selectedId = pigeonholePosition.id;
    this.selectedPigeonholeId = selectedId;
    this.selectedPigeonholeMap = null;
    this.isPigeonholeMapLoading = true;
    this.showPigeonholeMap = true;

    if (preloadedMap) {
      this.selectedPigeonholeMap = preloadedMap;
      this.isPigeonholeMapLoading = false;
      return;
    }

    try {
      const map = await this.pigeonholeService.getPigeonholePositionMap(selectedId);
      if (this.showPigeonholeMap && this.selectedPigeonholeId === selectedId) {
        this.selectedPigeonholeMap = map || [];
      }
    } catch (error) {
      console.log(error);
      if (this.selectedPigeonholeId === selectedId) {
        this.closePigeonholeMap();
      }
    }
  }

  closePigeonholeMap() {
    this.showPigeonholeMap = false;
    this.selectedPigeonholeId = null;
    this.selectedPigeonholeMap = null;
    this.isPigeonholeMapLoading = false;
    this.resetPigeonholeInternalSelection();
  }

  onPigeonholeMapLoaded() {
    this.isPigeonholeMapLoading = false;
  }

  fetchPigeonholePositions() {
    if (!this.map) {
      this.resetPigeonholeListNavigation();
      this.pigeonholePositions = [];
      return;
    }

    const pigeonholes = this.map.filter(p => p && p.type === 'PIGEONHOLE');
    this.pigeonholePositions = pigeonholes;
    this.selectedPositionIds = new Set<string>(pigeonholes.map(p => p.rfId));
  }

  async loadChildPigeonholePositions(pigeonholePosition): Promise<PigeonholeMapPosition[] | null> {
    if (!pigeonholePosition?.id || this.isPigeonholeListLoading) {
      return null;
    }

    this.isPigeonholeListLoading = true;

    try {
      const childPositions = await this.pigeonholeService.getPigeonholePositionMap(pigeonholePosition.id);
      return childPositions || [];
    } catch (error) {
      console.log(error);
      return null;
    } finally {
      this.isPigeonholeListLoading = false;
    }
  }

  resetPigeonholeListNavigation() {
    this.isPigeonholeListLoading = false;
  }

  setScreenState(screenState: SetupGroundScreenState) {
    this.screenState = screenState;
    this.showSectorsList = screenState !== SetupGroundScreenState.PIGEONHOLE_NAVIGATION;
    this.showPigeonholeList = screenState === SetupGroundScreenState.PIGEONHOLE_NAVIGATION;
    this.showPigeonholeMap = screenState === SetupGroundScreenState.PIGEONHOLE_INTERNAL_ASSIGNMENT;
  }

  resetCurrentGroundGroupSelection() {
    this.currentGroundGroupId = null;
    this.currentGroundGroupName = '';
  }

  resetPigeonholeInternalSelection() {
    this.pigeonholePositionsIds.clear();
    this.pigeonholePositionsIds = new Set<string>(this.pigeonholePositionsIds);
  }

  resetGeneralSelection() {
    this.selectedPositionIds.clear();
    this.selectedPositionIds = new Set<string>(this.selectedPositionIds);
  }

  returnToGeneralOverview() {
    this.selectedView = 'SECTORS';
    this.state = GroundGroupsMapState.DEFAULT;
    this.setScreenState(SetupGroundScreenState.GENERAL_OVERVIEW);
    this.closePigeonholeMap();
    this.resetPigeonholeListNavigation();
    this.resetCurrentGroundGroupSelection();
    this.resetPigeonholeInternalSelection();
    this.resetGeneralSelection();
  }
  
  canClickButton(action: ActionsEnum, role: UserRoles) {
    return this.actionPermissionsService.hasPermission(action, role);
  }

}
